import { useCallback, useEffect, useRef, useState } from "react";
import { WebViewerInstance } from "@pdftron/webviewer";

import { useFileViewerInstance } from "./FileViewer";
import { drawSourceAnnotations, SourceLocation } from "./fileViewerAnnotations";

/**
 * Highlights the citation locations and reveals the first cited page. `jumpToAnnotation`
 * (inside `drawSourceAnnotations`) silently no-ops on a page outside the render window, and
 * the `annotationsDrawn` event it waits on never fires for an off-screen page — so bring the
 * page into view first. The no-bbox path already uses `setCurrentPage`, so only bbox
 * locations need the nudge.
 */
function drawAndReveal(instance: WebViewerInstance, locations: SourceLocation[]) {
  const targetPage = locations.find((l) => l.bbox)?.pageNumber;
  if (targetPage !== undefined) instance.Core.documentViewer.setCurrentPage(targetPage, false);
  drawSourceAnnotations(instance, locations);
}

/**
 * Resolves `documentReady` once the viewer has finished its document lifecycle.
 * `annotationsLoaded` is the canonical ready signal — `documentLoaded` fires earlier
 * and gives incomplete results (see DocumentSearch.tsx). Programmatic jumps issued
 * before this is true land on a not-yet-laid-out viewer and silently no-op.
 */
export function useFileViewerReady() {
  const { instance } = useFileViewerInstance();
  const [documentReady, setDocumentReady] = useState(false);

  useEffect(() => {
    if (!instance) {
      setDocumentReady(false);
      return;
    }
    const { documentViewer } = instance.Core;

    // Treat a new instance / new document load as not-ready until annotationsLoaded fires.
    setDocumentReady(false);

    const onReady = () => setDocumentReady(true);
    const onDocumentLoaded = () => setDocumentReady(false);

    documentViewer.addEventListener("documentLoaded", onDocumentLoaded);
    documentViewer.addEventListener("annotationsLoaded", onReady);

    // Cover the race where annotationsLoaded already fired before we subscribed.
    if (documentViewer.getDocument()) setDocumentReady(true);
    return () => {
      documentViewer.removeEventListener("documentLoaded", onDocumentLoaded);
      documentViewer.removeEventListener("annotationsLoaded", onReady);
    };
  }, [instance]);

  return { instance, documentReady };
}

/**
 * Returns a callback that highlights and jumps the viewer to citation locations.
 * A click that lands before the viewer is ready is stashed and drawn once it
 * becomes ready, so the first click after opening the drawer is never dropped.
 */
export function useCitationNavigator() {
  const { instance, documentReady } = useFileViewerReady();
  const pendingRef = useRef<SourceLocation[] | null>(null);

  useEffect(() => {
    if (instance && documentReady && pendingRef.current) {
      drawAndReveal(instance, pendingRef.current);
      pendingRef.current = null;
    }
  }, [instance, documentReady]);

  return useCallback(
    (locations: SourceLocation[]) => {
      if (!locations.length) return;
      if (instance && documentReady) {
        drawAndReveal(instance, locations);
        return;
      }
      pendingRef.current = locations;
    },
    [instance, documentReady],
  );
}
